/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  Input,
  ChangeDetectionStrategy
} from '@angular/core';
import {
    SettlementDetailHeaderConfigurationModel
} from '../services/settlement-detail-configuration/models/settlement-detail-header-configuration.model';
import SETTLEMENT_DISPLAY_CONFIGURATION_CONSTANTS from '../services/settlement-detail-configuration/settlement-display-constants';
import pdfMake from 'pdfmake/build/pdfmake';
import {ActivatedRoute} from '@angular/router';
import {BusinessConfigurationEnum} from '../settlements-main-page/models/business-configuration.enum';
import genericPdfConfiguration from '../../shared/pdf/configuration/generic-pdf-configuration';
import moment from 'moment';
import {CustomerBaseModel} from '../models/settlement-detail/customer-base.model';
import {SettlementDetailModel} from '../models/settlement-detail/settlement-detail.model';
import {PrettyPrintReferencePipe} from '../../shared/pipes/pretty-print-reference/pretty-print-reference.pipe';
import {UndefinedValuePipe} from '../../shared/pipes/undefined-value/undefined-value.pipe';
import {PrettyPrintMaterialNumberPipe} from '../../shared/pipes/pretty-print-material-number/pretty-print-material-number.pipe';
import {PrettyPrintUpdatedByPipe} from '../../shared/pipes/pretty-print-updated-by/pretty-print-updated-by.pipe';
import {ReferenceDataModel} from '../../api/reference-data.model';
import {UserBaseModel} from '../../api/generic/models/user-base.model';
import {SettlementPositionBaseContract} from '../api/contracts/settlement-position-base.contract';
import {formatWithCurrencySymbol} from '../shared/util/financial-number/financial-number-helper';
import {
    footerPdfConfiguration,
    toDataURL
} from '../../shared/pdf/utils/generic-pdf-template-functions';
import {Column, Content, ContentCanvas, ContentColumns, TDocumentDefinitions} from 'pdfmake/interfaces';

@Component({
    selector: 'app-settlements-detail-header',
    templateUrl: './settlement-detail-header.component.html',
    styleUrls: ['./settlement-detail-header.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SettlementDetailHeaderComponent {
    @Input() configuration: SettlementDetailHeaderConfigurationModel;

    get exportPdfButtonLabel(): string {
        return SETTLEMENT_DISPLAY_CONFIGURATION_CONSTANTS.header.exportPdfButtonLabel;
    }

    constructor(private route: ActivatedRoute,
                private undefinedValuePipe: UndefinedValuePipe,
                private prettyPrintReferencePipe: PrettyPrintReferencePipe,
                private prettyPrintMaterialNumberPipe: PrettyPrintMaterialNumberPipe,
                private prettyPrintUpdatedByPipe: PrettyPrintUpdatedByPipe) {
    }

    async exportPdf(): Promise<void> {
        new Promise(async (resolve) => {
            const pdfName = this.getPdfFileName();
            const base64image = await toDataURL('/assets/ORCAN-logo.png')
                    .then((dataUrl: any) => dataUrl);

            pdfMake.createPdf(this.generatePdf(base64image))
                    .download(pdfName);
            resolve(null);
        });
    }

    private getPdfFileName(): string {
        const initial = this.isSupplier() ? 'SCS' : 'CCS';
        const complaintNumber = this.configuration.data.complaintCode ?? '/';
        const settlementNumber = this.configuration.data.settlementCode ?? '/';
        const status = this.configuration.data.status.name.toLowerCase();

        return `${initial}_${complaintNumber}-${settlementNumber}_${status}`;
    }

    private isSupplier(): boolean {
        return this.route.snapshot?.data['businessConfiguration'] === BusinessConfigurationEnum.SUPPLIER;
    }

    private generatePdf(footerImage: string): TDocumentDefinitions {
        const content: Content[] = [];
        content.push(this.settlementInformationSection(this.configuration.data));
        this.configuration.data.settlementPositions.forEach((settlement, index) => {
            content.push(this.settlementAgreementSection(settlement, this.configuration.data.currency.alphabeticCode, index));
        });
        content.push(this.settlementTotalAmountsSection(this.configuration.data));
        return {
            ...this.genericPdfConfiguration(),
            content,
            header: (): Content => this.headerPdfConfiguration(this.configuration.data.customer),
            footer: (currentPage: number, pageCount: number): Content => footerPdfConfiguration(currentPage, pageCount, footerImage)
        };
    }

    private headerPdfConfiguration(customer: CustomerBaseModel): Content {
        let contextTitle = 'Supplier';
        if (!this.isSupplier()) {
            contextTitle = this.prettyPrintReferencePipe.transform(customer);
        }
        const title = `${contextTitle} Complaint Settlement`;
        return [
            {
                columns: [
                    {
                        text: title.toUpperCase(),
                        width: '100%',
                        height: 20,
                        margin: [35, 35, 35, 0],
                        italics: true,
                        color: '#444'
                    }
                ]
            },
            {
                canvas: [
                    {type: 'line', x1: 35, y1: 0, x2: 560, y2: 0, lineWidth: 1, lineColor: '#444'}
                ]
            }
        ];
    }

    private settlementInformationSection(detail: SettlementDetailModel): Content {
        const lastPendingDate = this.transformDate(detail?.lastPendingDate)
                .toString();
        return [
            {
                stack: [
                    {...this.sectionTitle('Settlement information')},
                    {
                        columns: [
                            {
                                text: 'Complaint No.:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(detail?.complaintCode),
                                width: '25%',
                                style: 'value'
                            },
                            {
                                text: 'Last pending date:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: lastPendingDate,
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()},
                    {
                        columns: [
                            {
                                text: 'Settlement No.:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(detail?.settlementCode),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()},
                    {...this.statusTypeColumn(detail?.status, detail?.decidedBy)},
                    {...this.innerSectionHorizontalSeparator()},
                    {...this.reasonTypeColumn(detail?.status?.code, detail?.decisionReason)},
                    {...this.innerSectionHorizontalSeparator()},
                    {
                        columns: [
                            {
                                text: 'Supplier:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.prettyPrintReferencePipe.transform(this.configuration.data.supplier),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()},
                    {
                        columns: [
                            {
                                text: 'Part No.:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(this.prettyPrintMaterialNumberPipe.transform(detail?.material)),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()},
                    {
                        columns: [
                            {
                                text: 'Complaint description:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(detail?.complaintDescription),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.sectionHorizontalSeparator()}
                ],
                unbreakable: true
            }
        ];
    }

    private settlementAgreementSection(settlement: SettlementPositionBaseContract, currencyCode: string, index: number): Content {
        const contextTitle = 'Settlement agreement';
        return [
            {
                stack: [
                    {...this.sectionTitle(`${contextTitle} - Part block`, index + 1)},
                    {
                        columns: [
                            {
                                text: 'Quantity:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(settlement?.quantity),
                                width: '25%',
                                style: 'value'
                            },
                            {
                                text: 'Net price:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(
                                        formatWithCurrencySymbol(settlement?.materialsUnitNetPrice, currencyCode)),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()},
                    {
                        columns: [
                            {
                                text: 'Total net price:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(
                                        formatWithCurrencySymbol(settlement?.materialsTotalNetCost, currencyCode)),
                                width: '25%',
                                style: 'value'
                            },
                            {
                                text: 'Tax rate:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(settlement?.taxRate && `${settlement?.taxRate} %`),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()},
                    {
                        columns: [
                            {
                                text: 'Total amount:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(
                                        formatWithCurrencySymbol(settlement?.materialsTotalGrossCost, currencyCode)),
                                width: '25%',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()}
                ],
                unbreakable: true
            }, {
                stack: [
                    {...this.sectionTitle(`${contextTitle} - TE minutes block`)},
                    {
                        columns: [
                            {
                                text: 'Quantity:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(settlement?.teMinutesAmount),
                                width: '25%',
                                style: 'value'
                            },
                            {
                                text: 'Price per minute:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(
                                        formatWithCurrencySymbol(settlement?.teMinutesUnitCost, currencyCode)),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()},
                    {
                        columns: [
                            {
                                text: 'Additional costs:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(
                                        formatWithCurrencySymbol(settlement?.teMinutesAdditionalCosts, currencyCode)),
                                width: '25%',
                                style: 'value'
                            },
                            {
                                text: 'Total amount:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(
                                        formatWithCurrencySymbol(settlement?.teMinutesTotalCost, currencyCode)),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()}
                ],
                unbreakable: true
            }, {
                stack: [
                    {...this.sectionTitle(`${contextTitle} - Identity block`)},
                    {
                        columns: [
                            {
                                text: 'Blue Fapiao:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(settlement?.blueFapiaoNumber),
                                width: '25%',
                                style: 'value'
                            },
                            {
                                text: 'Delivery note:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(settlement?.deliveryNumber),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()},
                    {
                        columns: [
                            {
                                text: 'Comments:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(settlement?.comments),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.sectionHorizontalSeparator()}
                ],
                unbreakable: true
            }
        ];
    }

    private settlementTotalAmountsSection(detail: SettlementDetailModel): Content {
        return [
            {
                stack: [
                    {...this.sectionTitle('Settlement total amounts')},
                    {
                        columns: [
                            {
                                text: 'Settlement total amount:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(
                                        formatWithCurrencySymbol(detail?.settlementTotalCost, detail?.currency?.alphabeticCode)),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()},
                    {
                        columns: [
                            {
                                text: 'BCS contact manager:',
                                width: '25%',
                                style: 'label'
                            },
                            {
                                text: this.undefinedValuePipe.transform(detail?.contactManager),
                                width: 'auto',
                                style: 'value'
                            }
                        ]
                    },
                    {...this.innerSectionHorizontalSeparator()}
                ],
                unbreakable: true
            }
        ];
    }

    private genericPdfConfiguration(): TDocumentDefinitions {
        return {
            ...genericPdfConfiguration,
            styles: {
                label: {
                    fontSize: 11,
                    italics: true,
                    color: '#8f8f8f',
                    margin: [0, 8, 12, 4]
                },
                value: {
                    fontSize: 11,
                    margin: [0, 8, 0, 4]
                }
            }
        };
    }

    private reasonTypeColumn(status: string, reason: string): ContentColumns {
        const columns = [];
        if (status && status !== 'PENDING') {
            const reasonLabel = status === 'REJECTED' ? 'Reason for rejection' : 'Acceptance comment';
            columns.push(
                    {
                        text: `${reasonLabel}:`,
                        width: '25%',
                        style: 'label'
                    },
                    {
                        text: this.undefinedValuePipe.transform(reason),
                        width: 'auto',
                        style: 'value'
                    }
            );
        }
        return {columns};
    }

    private statusTypeColumn(status: ReferenceDataModel, user: UserBaseModel): ContentColumns {
        const columns = [
            {
                text: 'Status:',
                width: '25%',
                style: 'label'
            },
            {
                text: this.undefinedValuePipe.transform(status?.name),
                width: '25%',
                style: 'value'
            }
        ];

        if (status?.code !== 'PENDING') {
            columns.push({
                text: `${this.undefinedValuePipe.transform(status?.name)} by:`,
                width: 'auto',
                style: 'label'
            }, {
                text: this.prettyPrintUpdatedByPipe.transform(user),
                width: 'auto',
                style: 'value'
            });
        }

        return {columns};
    }

    private transformDate(date: number): string {
        if (date == null) {
            return '-';
        }
        return moment(date)
                .format('DD-MMM-YYYY | HH:mm:ss');
    }

    private sectionTitle(title: string, lineIndex?: number): ContentColumns {
        const columns: Column[] = [
            {
                text: title,
                width: '50%',
                height: 18,
                margin: [0, 6, 0, 2],
                color: '#444'
            }
        ];

        if (lineIndex) {
            columns.push({
                text: `Line ${lineIndex}`,
                width: '50%',
                height: 18,
                margin: [0, 6, 0, 2],
                color: '#444',
                alignment: 'right'
            });
        }

        return {columns};
    }

    private innerSectionHorizontalSeparator(): ContentCanvas {
        return {canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, lineColor: '#d8d8d8'}]};
    }

    private sectionHorizontalSeparator(): ContentCanvas {
        return {
            canvas: [{type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 1, lineColor: '#444'}]
        };
    }
}
